import { useEffect, useMemo, useState } from "react";
import { Briefcase, Calendar, ClipboardList, LogOut, Mail, Phone, Search, Trash2, User, Users } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const KEY = "tarzan-admin-token";
const STATUS = [
  { v: "new",        label: "New",        cls: "bg-forest text-ivory" },
  { v: "contacted",  label: "Contacted",  cls: "bg-sand-100 text-forest-700" },
  { v: "confirmed",  label: "Confirmed",  cls: "bg-forest-50 text-forest border border-forest/30" },
  { v: "cancelled",  label: "Cancelled",  cls: "bg-red-50 text-red-700 border border-red-200" },
];

const PARTNER_TYPES = {
  host:     "Property Owner",
  land:     "Land Partner",
  operator: "Hospitality Operator",
  collab:   "Cultural Collaborator",
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(KEY) || "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("bookings"); // bookings | partners
  const [items, setItems] = useState([]);
  const [partners, setPartners] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => { if (token) load(); /* eslint-disable-next-line */ }, [token]);

  const load = async () => {
    try {
      const [b, p] = await Promise.all([
        api.get("/admin/bookings", { headers: { "X-Admin-Token": token } }),
        api.get("/admin/partner-inquiries", { headers: { "X-Admin-Token": token } }),
      ]);
      setItems(b.data);
      setPartners(p.data);
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem(KEY); setToken(""); toast.error("Session expired. Please log in again."); }
    }
  };

  const login = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/admin/login", { password });
      localStorage.setItem(KEY, data.token);
      setToken(data.token);
      setPassword("");
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  const logout = () => { localStorage.removeItem(KEY); setToken(""); setItems([]); setPartners([]); };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}/status`, { status }, { headers: { "X-Admin-Token": token } });
      setItems(items.map(b => b.id === id ? { ...b, status } : b));
      toast.success(`Marked as ${status}.`);
    } catch { toast.error("Failed to update status."); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this enquiry?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`, { headers: { "X-Admin-Token": token } });
      setItems(items.filter(b => b.id !== id));
      toast.success("Enquiry removed.");
    } catch { toast.error("Delete failed."); }
  };

  const updatePartnerStatus = async (id, status) => {
    try {
      await api.patch(`/admin/partner-inquiries/${id}/status`, { status }, { headers: { "X-Admin-Token": token } });
      setPartners(partners.map(p => p.id === id ? { ...p, status } : p));
      toast.success(`Marked as ${status}.`);
    } catch { toast.error("Failed to update status."); }
  };

  const removePartner = async (id) => {
    if (!confirm("Delete this franchise enquiry?")) return;
    try {
      await api.delete(`/admin/partner-inquiries/${id}`, { headers: { "X-Admin-Token": token } });
      setPartners(partners.filter(p => p.id !== id));
      toast.success("Enquiry removed.");
    } catch { toast.error("Delete failed."); }
  };

  const source = tab === "bookings" ? items : partners;

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return source.filter(b => (
      (filter === "all" || b.status === filter) &&
      (!ql || `${b.name || b.guest_name} ${b.email} ${b.phone} ${b.property_name || b.inquiry_type || ""}`.toLowerCase().includes(ql))
    ));
  }, [source, q, filter]);

  const counts = useMemo(() => {
    const c = { all: source.length, new: 0, contacted: 0, confirmed: 0, cancelled: 0 };
    source.forEach(b => { c[b.status] = (c[b.status] || 0) + 1; });
    return c;
  }, [source]);

  if (!token) {
    return (
      <div className="pt-32 pb-24 bg-ivory min-h-screen">
        <div className="container-tarzan max-w-md">
          <div className="eyebrow mb-3">Admin</div>
          <h1 className="font-serif text-4xl md:text-5xl text-ink tracking-tight">Concierge Console</h1>
          <p className="text-ink-soft mt-3">Sign in to review booking and franchise enquiries.</p>
          <form onSubmit={login} className="bg-white border border-forest/10 rounded-3xl p-7 luxe-shadow mt-8 space-y-4" data-testid="admin-login">
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft font-semibold mb-1.5">Admin password</div>
              <input required type="password" value={password} onChange={(e)=>setPassword(e.target.value)} autoFocus data-testid="admin-password" className="w-full border border-forest/15 rounded-xl bg-transparent px-3 py-3 outline-none focus:border-forest text-sm text-ink" />
            </label>
            <button disabled={busy} data-testid="admin-login-submit" className="w-full bg-forest text-ivory py-3.5 rounded-full font-medium text-sm hover:bg-forest-700 transition-colors disabled:opacity-50">
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const heading = tab === "bookings" ? "Booking Enquiries" : "Franchise Enquiries";

  return (
    <div className="pt-32 pb-24 bg-ivory min-h-screen" data-testid="admin-dashboard">
      <div className="container-tarzan">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <div className="eyebrow mb-3">Concierge Console</div>
            <h1 className="font-serif text-4xl md:text-5xl text-ink tracking-tight">{heading}</h1>
            <p className="text-ink-soft mt-2 num">{counts.all} total · {counts.new} new · {counts.contacted} contacted · {counts.confirmed} confirmed</p>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="self-start inline-flex items-center gap-2 text-sm text-forest border border-forest/20 rounded-full px-5 py-2.5 hover:bg-forest hover:text-ivory transition-colors">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>

        {/* Tab switcher */}
        <div className="mt-8 inline-flex items-center bg-white border border-forest/15 rounded-full p-1" data-testid="admin-tabs">
          {[
            { k: "bookings", label: "Bookings",  Icon: ClipboardList, count: items.length },
            { k: "partners", label: "Franchise", Icon: Briefcase,     count: partners.length },
          ].map(({ k, label, Icon, count }) => (
            <button
              key={k}
              onClick={() => { setTab(k); setFilter("all"); setQ(""); }}
              data-testid={`admin-tab-${k}`}
              className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-xs uppercase tracking-[0.18em] font-semibold transition-colors ${tab === k ? "bg-forest text-ivory" : "text-ink hover:text-forest"}`}
            >
              <Icon className="h-3.5 w-3.5" /> {label} <span className="num text-[10px] opacity-75">({count})</span>
            </button>
          ))}
        </div>

        <div className="mt-8 flex flex-col md:flex-row gap-3 md:items-center">
          <div className="flex items-center gap-2 flex-1 bg-white border border-forest/15 rounded-full px-4 py-2.5">
            <Search className="h-4 w-4 text-forest-400" />
            <input placeholder={tab === "bookings" ? "Search by name, email, phone or property" : "Search by name, email, phone or type"} value={q} onChange={(e)=>setQ(e.target.value)} data-testid="admin-search" className="flex-1 bg-transparent outline-none text-sm" />
          </div>
          <div className="flex flex-wrap gap-2">
            {["all", "new", "contacted", "confirmed", "cancelled"].map(k => (
              <button key={k} onClick={()=>setFilter(k)} data-testid={`admin-filter-${k}`}
                className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider border transition-colors ${filter === k ? "bg-forest text-ivory border-forest" : "border-forest/20 text-ink hover:border-forest"}`}>
                {k} <span className="num">({counts[k] ?? 0})</span>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 space-y-3">
          {filtered.length === 0 && (
            <div className="bg-white border border-dashed border-forest/20 rounded-3xl p-14 text-center">
              <div className="font-serif text-2xl text-ink">No {tab === "bookings" ? "enquiries" : "franchise enquiries"} yet.</div>
              <p className="text-ink-soft mt-2 text-sm">
                {tab === "bookings"
                  ? "Enquiries submitted from any property page will appear here in real time."
                  : "Franchise enquiries submitted from the Partner With Us page will appear here in real time."}
              </p>
            </div>
          )}

          {tab === "bookings" && filtered.map(b => {
            const status = STATUS.find(s => s.v === b.status) || STATUS[0];
            return (
              <div key={b.id} className="bg-white border border-forest/10 rounded-2xl soft-shadow p-5 md:p-6" data-testid={`admin-booking-${b.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-[0.22em] font-semibold px-3 py-1 rounded-full ${status.cls}`}>{status.label}</span>
                      <span className="font-serif text-xl text-ink">{b.guest_name}</span>
                      <span className="text-xs text-ink-soft">· {b.property_name}</span>
                    </div>
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <Info icon={Mail}     label="Email"    v={<a href={`mailto:${b.email}`} className="text-forest hover:underline break-all">{b.email}</a>} />
                      <Info icon={Phone}    label="Phone"    v={<a href={`tel:${b.phone}`} className="text-forest hover:underline num">{b.phone}</a>} />
                      <Info icon={Calendar} label="Dates"    v={<span className="num">{b.checkin_date} → {b.checkout_date}</span>} />
                      <Info icon={Users}    label="Guests"   v={<span className="num">{b.num_guests} @ {b.checkin_time}</span>} />
                    </div>
                    {b.notes && (
                      <div className="mt-3 bg-forest-50/60 rounded-xl px-4 py-3 text-sm text-ink">
                        <span className="text-[10px] uppercase tracking-[0.18em] text-ink-soft font-semibold mr-2">Notes</span>
                        {b.notes}
                      </div>
                    )}
                    <div className="text-[11px] text-ink-soft mt-3 num">Received {new Date(b.created_at).toLocaleString()}</div>
                  </div>
                  <div className="flex flex-wrap lg:flex-col gap-2 shrink-0">
                    {STATUS.filter(s => s.v !== b.status).map(s => (
                      <button key={s.v} onClick={()=>updateStatus(b.id, s.v)} data-testid={`admin-set-${s.v}-${b.id}`}
                        className="text-xs font-medium px-4 py-2 rounded-full border border-forest/20 hover:border-forest hover:bg-forest hover:text-ivory transition-colors">
                        Mark {s.label}
                      </button>
                    ))}
                    <button onClick={()=>remove(b.id)} data-testid={`admin-delete-${b.id}`}
                      className="text-xs font-medium px-4 py-2 rounded-full border border-red-200 text-red-700 hover:bg-red-600 hover:text-ivory hover:border-red-600 transition-colors inline-flex items-center gap-1.5">
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {tab === "partners" && filtered.map(p => {
            const status = STATUS.find(s => s.v === p.status) || STATUS[0];
            return (
              <div key={p.id} className="bg-white border border-forest/10 rounded-2xl soft-shadow p-5 md:p-6" data-testid={`admin-partner-${p.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-[0.22em] font-semibold px-3 py-1 rounded-full ${status.cls}`}>{status.label}</span>
                      <span className="font-serif text-xl text-ink">{p.name}</span>
                      <span className="text-xs text-ink-soft">· {PARTNER_TYPES[p.inquiry_type] || p.inquiry_type}</span>
                    </div>
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <Info icon={Mail}     label="Email"    v={<a href={`mailto:${p.email}`} className="text-forest hover:underline break-all">{p.email}</a>} />
                      <Info icon={Phone}    label="Phone"    v={<a href={`tel:${p.phone}`} className="text-forest hover:underline num">{p.phone}</a>} />
                      <Info icon={Briefcase} label="Enquiry type" v={PARTNER_TYPES[p.inquiry_type] || p.inquiry_type} />
                    </div>
                    {p.message && (
                      <div className="mt-3 bg-forest-50/60 rounded-xl px-4 py-3 text-sm text-ink">
                        <span className="text-[10px] uppercase tracking-[0.18em] text-ink-soft font-semibold mr-2">Message</span>
                        {p.message}
                      </div>
                    )}
                    <div className="text-[11px] text-ink-soft mt-3 num">Received {new Date(p.created_at).toLocaleString()}</div>
                  </div>
                  <div className="flex flex-wrap lg:flex-col gap-2 shrink-0">
                    {STATUS.filter(s => s.v !== p.status).map(s => (
                      <button key={s.v} onClick={()=>updatePartnerStatus(p.id, s.v)} data-testid={`admin-partner-set-${s.v}-${p.id}`}
                        className="text-xs font-medium px-4 py-2 rounded-full border border-forest/20 hover:border-forest hover:bg-forest hover:text-ivory transition-colors">
                        Mark {s.label}
                      </button>
                    ))}
                    <button onClick={()=>removePartner(p.id)} data-testid={`admin-partner-delete-${p.id}`}
                      className="text-xs font-medium px-4 py-2 rounded-full border border-red-200 text-red-700 hover:bg-red-600 hover:text-ivory hover:border-red-600 transition-colors inline-flex items-center gap-1.5">
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft font-semibold inline-flex items-center gap-1.5"><Icon className="h-3 w-3" /> {label}</div>
      <div className="mt-1 text-sm text-ink">{v}</div>
    </div>
  );
}
